<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.POLICY.2 — "¿Exigir SSO?" (canvas: Enforce). Shows what
// changes, picks the emergency-access administrator and asks for an explicit
// confirmation; the server re-checks all of it (and refuses to lock the acting
// administrator out).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import { useKaptionSecurityText } from '../useKaptionSecurityText';

const { t } = useI18n();
const { overview, setOverview } = useKaptionSecurity();
const { provider, domainsText } = useKaptionSecurityText(overview);
const currentUserId = useMapGetter('getCurrentUserID');
const currentAccount = useMapGetter('getCurrentAccount');

const dialogRef = ref(null);
const emergencyId = ref('');
const confirmed = ref(false);
const busy = ref(false);
const error = ref('');

const admins = computed(() => overview.value?.admins || []);
const impact = computed(() => overview.value?.impact || {});

const rows = computed(() => {
  const vars = { domains: domainsText.value, provider: provider.value };
  return [
    {
      n: impact.value.in_domain,
      tone: 'text-n-slate-12',
      title: t('KAPTION_SECURITY.ENFORCE.IN_DOMAIN_TITLE', vars),
      desc: t('KAPTION_SECURITY.ENFORCE.IN_DOMAIN_DESC', vars),
    },
    {
      n: impact.value.sso_signed_in,
      tone: 'text-n-teal-11',
      title: t('KAPTION_SECURITY.ENFORCE.SSO_TITLE', vars),
      desc: t('KAPTION_SECURITY.ENFORCE.SSO_DESC', vars),
    },
    {
      n: impact.value.outside_domain,
      tone: 'text-n-amber-11',
      title: t('KAPTION_SECURITY.ENFORCE.OUTSIDE_TITLE', vars),
      desc: t('KAPTION_SECURITY.ENFORCE.OUTSIDE_DESC', vars),
    },
    impact.value.multi_account > 0 && {
      n: impact.value.multi_account,
      tone: 'text-n-amber-11',
      title: t('KAPTION_SECURITY.ENFORCE.MULTI_TITLE', vars),
      desc: t('KAPTION_SECURITY.ENFORCE.MULTI_DESC', vars),
    },
    {
      n: impact.value.tokens,
      tone: 'text-n-amber-11',
      title: t('KAPTION_SECURITY.ENFORCE.TOKENS_TITLE', vars),
      desc: t('KAPTION_SECURITY.ENFORCE.TOKENS_DESC', vars),
    },
  ].filter(Boolean);
});

const open = () => {
  const current = overview.value?.settings?.emergency_user_id;
  emergencyId.value = String(current || currentUserId.value || '');
  confirmed.value = false;
  error.value = '';
  dialogRef.value?.open();
};

const submit = async () => {
  if (!confirmed.value) return;
  busy.value = true;
  error.value = '';
  try {
    const { data } = await KaptionSecurityAPI.updatePolicy({
      sign_in_policy: 'sso_required',
      emergency_user_id: emergencyId.value ? Number(emergencyId.value) : null,
      confirm: true,
    });
    setOverview(data);
    useAlert(
      t('KAPTION_SECURITY.ENFORCE.DONE', { domains: domainsText.value })
    );
    dialogRef.value?.close();
  } catch (e) {
    const code = e?.response?.data?.error_code;
    const known = [
      'would_lock_you_out',
      'connection_not_enabled',
      'no_verified_domain',
      'confirmation_required',
    ];
    error.value = known.includes(code)
      ? t(`KAPTION_SECURITY.ENFORCE.ERRORS.${code}`, {
          provider: provider.value,
        })
      : t('KAPTION_SECURITY.ENFORCE.ERRORS.generic');
  } finally {
    busy.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KAPTION_SECURITY.ENFORCE.TITLE', { domains: domainsText })"
    :description="
      t('KAPTION_SECURITY.ENFORCE.DESC', {
        domains: domainsText,
        account: currentAccount?.name || '',
        provider,
      })
    "
    :confirm-button-label="t('KAPTION_SECURITY.ENFORCE.SUBMIT')"
    :disable-confirm-button="!confirmed || busy"
    :is-loading="busy"
    width="xl"
    overflow-y-auto
    @confirm="submit"
  >
    <div class="flex flex-col gap-5">
      <div class="border rounded-xl border-n-weak">
        <div
          v-for="(row, index) in rows"
          :key="row.title"
          class="flex gap-4 px-4 py-3"
          :class="{ 'border-t border-n-weak': index > 0 }"
        >
          <span
            class="w-10 text-lg font-semibold text-right shrink-0"
            :class="row.tone"
            >{{ row.n ?? 0 }}</span
          >
          <span class="flex flex-col gap-0.5">
            <span class="text-sm font-semibold text-n-slate-12">{{
              row.title
            }}</span>
            <span class="text-sm text-n-slate-11">{{ row.desc }}</span>
          </span>
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <label for="ks-emergency" class="text-sm font-medium text-n-slate-12">
          {{ t('KAPTION_SECURITY.ENFORCE.EMERGENCY_LABEL') }}
        </label>
        <select
          id="ks-emergency"
          v-model="emergencyId"
          class="h-10 px-3 text-sm border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12"
        >
          <option
            v-for="admin in admins"
            :key="admin.id"
            :value="String(admin.id)"
          >
            {{
              t('KAPTION_SECURITY.ENFORCE.EMERGENCY_OPTION', {
                name: admin.name || admin.email,
              })
            }}
          </option>
          <option value="">
            {{ t('KAPTION_SECURITY.ENFORCE.EMERGENCY_NONE') }}
          </option>
        </select>
        <span class="text-[13px] text-n-slate-11">
          {{ t('KAPTION_SECURITY.ENFORCE.EMERGENCY_HELP', { provider }) }}
        </span>
      </div>

      <label class="flex gap-3 text-sm cursor-pointer text-n-slate-12">
        <input
          v-model="confirmed"
          type="checkbox"
          class="mt-0.5 accent-n-brand"
          data-testid="ks-enforce-confirm"
        />
        <span>
          {{
            t('KAPTION_SECURITY.ENFORCE.CONFIRM', {
              count: impact.in_domain ?? 0,
              domains: domainsText,
              provider,
            })
          }}
        </span>
      </label>

      <p
        v-if="error"
        class="px-3 py-2 m-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{ error }}
      </p>
    </div>
  </Dialog>
</template>
